/**
 * Every write Edit › Reels makes, in one place (CAE-1552).
 *
 * Lifted out of `contest-reel-options-panel.tsx` rather than rewritten — same
 * endpoints, same arguments, same notices, in the order the old panel declared
 * them. CAE-1552's first acceptance criterion is that this behaviour is
 * *preserved*, and the cheapest way to keep that promise is to move the code
 * instead of retyping it.
 *
 * A hook rather than a module of functions because each one is a
 * `useMutation` — they need the query client to bust the contest payload after
 * a write, and `isPending` is what disables the control that fired them.
 *
 * **Both caches, every time.** The contest detail is what this screen renders;
 * `['admin', 'contests', id]` is what the Contest Overview renders. An operator
 * who toggles an option here and clicks back to the Overview would otherwise be
 * reading a reel cached before they touched it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  contestsApi,
  reelKindForWrite,
  type ContestDetailReel,
  type ReelKind,
  type ReelOptionParticipantPayload,
} from '../../../../api/contests';
import { getPrefixedErrorMessage } from '../../../../utils/error-message';
import type { OutcomePick } from './add-outcomes-picks';

/**
 * What the operator is told after a batch, which depends on how much of it
 * landed. Named outcomes rather than a bare count, because "1 failed" leaves
 * them to find out which by reading the reel against their own selection.
 */
const addOutcomesNotice = ({
  added,
  failed,
  total,
  reelNumber,
}: {
  added: number;
  failed: readonly string[];
  total: number;
  reelNumber: number;
}): string => {
  const noun = (count: number) => (count === 1 ? 'outcome' : 'outcomes');
  if (failed.length === 0) return `Added ${added} ${noun(added)} to reel ${reelNumber}.`;

  const names = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(failed);
  if (added === 0) return `Couldn’t add any outcomes to reel ${reelNumber}. ${names} failed.`;
  return `Added ${added} of ${total} outcomes to reel ${reelNumber}. ${names} failed.`;
};

export interface ReelOptionTarget {
  reelNumber: number;
  optionId: string;
}

export interface UseReelOptionMutationsOptions {
  contestId: string;
  contestDetailQueryKey: readonly unknown[];
  onNotice: (message: string) => void;
}

export const useReelOptionMutations = ({
  contestId,
  contestDetailQueryKey,
  onNotice,
}: UseReelOptionMutationsOptions) => {
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: contestDetailQueryKey });
    await queryClient.invalidateQueries({ queryKey: ['admin', 'contests', contestId] });
  };

  const setEnabled = useMutation({
    mutationFn: (input: ReelOptionTarget & { enabled: boolean }) =>
      contestsApi.updateReelOptionEnabled(
        contestId,
        input.reelNumber,
        input.optionId,
        input.enabled,
      ),
    onSuccess: async () => {
      await invalidate();
      onNotice('Option status updated.');
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to update option status', error)),
  });

  const setDisplayNameOverride = useMutation({
    mutationFn: (input: ReelOptionTarget & { displayNameOverride: string }) => {
      const trimmed = input.displayNameOverride.trim();
      return contestsApi.updateReelOptionDisplayNameOverride(
        contestId,
        input.reelNumber,
        input.optionId,
        trimmed === '' ? null : trimmed,
      );
    },
    onSuccess: async () => {
      await invalidate();
      onNotice('Display name override saved.');
    },
    onError: (error) =>
      onNotice(getPrefixedErrorMessage('Failed to save display name override', error)),
  });

  const setAsset = useMutation({
    mutationFn: (input: ReelOptionTarget & { assetId: string | null }) =>
      contestsApi.updateReelOptionAsset(contestId, input.reelNumber, input.optionId, input.assetId),
    onSuccess: async () => {
      await invalidate();
      onNotice('Asset updated.');
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to update asset', error)),
  });

  const setParticipant = useMutation({
    mutationFn: (input: ReelOptionTarget & { participant: ReelOptionParticipantPayload }) =>
      contestsApi.updateReelOptionParticipant(
        contestId,
        input.reelNumber,
        input.optionId,
        input.participant,
      ),
    onSuccess: async () => {
      await invalidate();
      onNotice('Club and number saved.');
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to save club and number', error)),
  });

  const remove = useMutation({
    mutationFn: (input: ReelOptionTarget) =>
      contestsApi.removeReelOption(contestId, input.reelNumber, input.optionId),
    onSuccess: async () => {
      await invalidate();
      onNotice('Option removed.');
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to remove option', error)),
  });

  const reorder = useMutation({
    mutationFn: (input: ReelOptionTarget & { position: number }) =>
      contestsApi.reorderReelOption(contestId, input.reelNumber, input.optionId, input.position),
    onSuccess: async () => {
      await invalidate();
      onNotice('Option order updated.');
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to reorder option', error)),
  });

  const renameReel = useMutation({
    mutationFn: (input: {
      reelNumber: number;
      label: string;
      reel: ContestDetailReel | null;
      /** The kind the Operator chose, for a Reel this rename creates. */
      kind?: ReelKind;
    }) => {
      const trimmed = input.label.trim();
      return contestsApi.upsertReel(contestId, input.reelNumber, {
        ...reelKindForWrite(input.reel, input.kind),
        label: trimmed === '' ? null : trimmed,
      });
    },
    onSuccess: async () => {
      await invalidate();
      onNotice('Reel name saved.');
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to save reel name', error)),
  });

  /**
   * The Add Outcomes modal's confirm (CAE-1553): one operator action, one
   * create per picked outcome.
   *
   * **The Reel is made first, if it is not there.** CAE-1566 closed the door
   * this used to walk through: adding a catalogue selection to a Reel that does
   * not exist is now a 404, where it used to create the Reel inside the same
   * transaction. It stopped doing that because a Reel conjured by an option
   * append had no `kind`, and every Reel now declares one. So the upsert runs
   * first and `reelKindForWrite` supplies Game Market for a Reel this call is
   * creating, nothing for one that already exists — which then keeps its own.
   * This is the order `catalogue-page.tsx`'s reel mode used before CAE-1553
   * replaced it, kept because the contract still wants it.
   *
   * Its failure is the whole batch's: with no Reel there is nothing to append
   * to, and reporting eight separate 404s would describe the symptom rather
   * than the cause. So it is the one step here allowed to reject.
   *
   * **The appends stay sequential.** Each reads `max(position) + 1` for its
   * Reel, so parallel issues collide on one position — the server now locks the
   * Reel row, which serialises them, but the order they land in is then the
   * order they happened to arrive, not the order the operator picked them. A
   * loop keeps the reel reading the way the selection did.
   *
   * **A failure does not abandon the rest.** One rejected outcome — a duplicate
   * the server caught, a dropped connection — says nothing about the outcomes
   * after it, and stopping would leave a part-filled reel with no account of
   * where it gave up. So the batch runs to the end and reports what landed,
   * which is why this never rejects on an append: some of it succeeded, and the
   * caches have to be busted either way.
   */
  const addOutcomes = useMutation({
    mutationFn: async (input: {
      reelNumber: number;
      picks: readonly OutcomePick[];
      /** The Reel being added to, or null when it has never been configured. */
      reel: ContestDetailReel | null;
      /** The kind the Operator chose, for a Reel this call creates. */
      kind?: ReelKind;
    }) => {
      await contestsApi.upsertReel(contestId, input.reelNumber, {
        ...reelKindForWrite(input.reel, input.kind),
        label: input.reel?.label ?? null,
      });

      let added = 0;
      const failed: string[] = [];
      for (const pick of input.picks) {
        try {
          await contestsApi.createReelOptionFromCatalogue(contestId, input.reelNumber, pick);
          added += 1;
        } catch {
          failed.push(pick.outcomeName);
        }
      }
      return { added, failed, total: input.picks.length, reelNumber: input.reelNumber };
    },
    onSuccess: async ({ added, failed, total, reelNumber }) => {
      await invalidate();
      onNotice(addOutcomesNotice({ added, failed, total, reelNumber }));
    },
    onError: (error) => onNotice(getPrefixedErrorMessage('Failed to add outcomes', error)),
  });

  return {
    setEnabled,
    setParticipant,
    setDisplayNameOverride,
    setAsset,
    remove,
    reorder,
    renameReel,
    addOutcomes,
  };
};

export type ReelOptionMutations = ReturnType<typeof useReelOptionMutations>;
